import webstomp, { Client, Message, Subscription, SubscriptionsMap, ConnectionHeaders } from 'webstomp-client';
import { EnhancedStore } from '@reduxjs/toolkit/src/configureStore';
import { QContext, REACT_APP_QWEALTH_QC_API } from '@qwealth/qdata';
import { Message as QMessage } from 'data/models/Message';
import { putMessages } from '../data/actions/messages';

export class WebSocketService {
  private static _instance: WebSocketService = new WebSocketService();

  private _websocketUrl = `${(REACT_APP_QWEALTH_QC_API ?? '').replace('https', 'wss').replace('http', 'ws').replace('/v1', '')}`;
  private _email?: string;
  private _idToken?: string;
  private _client: Client | null = null;
  private _store: EnhancedStore | null = null;
  private _connecting = false;
  private _messageQueue: Array<{ destination: string; body: string }> = [];

  private constructor() {}

  private processMessageResponse = (messageOutput: Message, resolve?: () => void): void => {
    const messages = JSON.parse(messageOutput.body) as Array<QMessage>;
    const filteredMessages = messages.filter(({ recipient }) => recipient.toLowerCase() === this._email?.toLowerCase());
    this._store?.dispatch(putMessages(filteredMessages));
    resolve?.();
  };

  private awaitFirstMessage = (): Promise<void> => {
    return new Promise(resolve => {
      this._client?.subscribe('/topic/messages', message => this.processMessageResponse(message, resolve), { user_uuid: QContext.getUserUuid() });
    });
  };

  private onConnected = (): void => {
    console.log('WebSocket Connected!');
    WebSocketService.subscribe('/topic/messages', this.processMessageResponse);
    this._connecting = false;
    this.flushQueue();
  };

  private async onReconnected(resolve: () => void): Promise<void> {
    console.log('WebSocket Connected!');
    await this.awaitFirstMessage(); // first message is the connection response with all notifications
    this._connecting = false;
    this.flushQueue();
    resolve();
  }

  private flushQueue = (): void => {
    if (this._client?.connected === true) {
      while (this._messageQueue.length > 0) {
        const { destination, body } = this._messageQueue.shift()!;
        this._client.send(destination, body);
      }
    }
  };

  private onError = (error: unknown): void => {
    console.error('WebSocket connection failed:', error);
    this._connecting = false;
    this._client = null;
  };

  private createClient = (): void => {
    this._client = webstomp.client(`${this._websocketUrl}/notification?token=${this._idToken}`, { debug: false });
  };

  private getHeaders = (): ConnectionHeaders => ({ Authorization: `Bearer ${this._idToken}`, user_uuid: QContext.getUserUuid() });

  private connectClient(): void {
    if (this._connecting) {
      return;
    }
    try {
      if (this._client?.connected === true) {
        this._client.disconnect(() => console.log('Disconnected from WS...'));
      }
      this._connecting = true;
      console.log('Connecting to WS...');
      this.createClient();
      this._client && this._client.connect(this.getHeaders(), this.onConnected, this.onError);
    } catch (e) {
      this.onError(e);
    }
  }

  private reconnectClient(): Promise<void> {
    if (this._client?.connected === true) {
      console.warn('Already connected');
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      this._connecting = true;
      try {
        this.createClient();
        this._client?.connect(
          this.getHeaders(),
          () => this.onReconnected(resolve),
          error => {
            this.onError(error);
            reject(error);
          },
        );
      } catch (e) {
        this.onError(e);
        reject(e);
      }
    });
  }

  public static getInstance(): WebSocketService {
    return WebSocketService._instance;
  }

  public static getIdToken(): string | undefined {
    return this.getInstance()._idToken;
  }

  public static setReduxStore(store: EnhancedStore): void {
    this._instance._store = store;
  }

  public static connect(email: string, idToken: string): void {
    this._instance._email = email;
    this._instance._idToken = idToken;
    this._instance.connectClient();
  }

  public static async reconnect(): Promise<void> {
    return await this._instance.reconnectClient();
  }

  public static send(destination: string, message: Record<string, unknown>): void {
    if (this._instance._client?.connected !== true) {
      console.warn('client unavailable!');
      this._instance._messageQueue.push({ destination, body: JSON.stringify(message) });
      this._instance.connectClient();
    }

    if (this._instance._client?.connected === true) {
      this._instance._client.send(destination, JSON.stringify(message));
    }
  }

  public static subscribe(destination: string, callback?: (message: Message) => void): Subscription | null {
    if (this._instance._client?.connected !== true) {
      console.warn('client unavailable!');
      return null;
    }
    return this._instance._client.subscribe(destination, callback, { user_uuid: QContext.getUserUuid() });
  }

  public static getSubscriptions(): SubscriptionsMap {
    if (this._instance._client?.connected !== true) {
      console.warn('client unavailable!');
      return {};
    }
    return this._instance._client.subscriptions;
  }
}
